import React from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import { Info } from "lucide-react";
import Eclipse from "@/assets/svg/Eclipse";
import { renderTextWithLinks } from "@/shared/utils/helperFunctions";
const cn = (...inputs) => twMerge(clsx(inputs));

// Each variant carries the same soft inset highlight (white on top, a tint of its own color
// underneath). Class strings are written out in full so Tailwind can see them.
const variantClassesWithBg = {
  success: "bg-success-50 dark:bg-emerald-950/70 text-success-500 dark:text-emerald-400 shadow-[0_6px_4px_0_rgba(255,255,255,0.25)_inset,0_-2px_4px_0_rgba(18,183,106,0.20)_inset]",
  warning: "bg-warning-50 dark:bg-amber-950/70 text-warning-500 dark:text-amber-400 shadow-[0_6px_4px_0_rgba(255,255,255,0.25)_inset,0_-2px_4px_0_rgba(247,144,9,0.20)_inset]",
  danger: "bg-error-50 dark:bg-rose-950/70 text-error-500 dark:text-rose-400 shadow-[0_6px_4px_0_rgba(255,255,255,0.25)_inset,0_-2px_4px_0_rgba(240,68,56,0.20)_inset]",
  neutral: "bg-gray-50 dark:bg-gray-800/80 text-gray-500 dark:text-gray-300 shadow-[0_6px_4px_0_rgba(255,255,255,0.25)_inset,0_-2px_4px_0_rgba(113,118,128,0.20)_inset]",
  lightBlue: "bg-[#207C971A] dark:bg-[#207C9730] text-[#207C97] dark:text-secondary-light shadow-[0_6px_4px_0_rgba(255,255,255,0.25)_inset,0_-2px_4px_0_rgba(32,124,151,0.20)_inset]",
};

const variantClassesWithoutBg = {
  success: {
    InnerColor: "#12B76A",
    OuterColor: "rgba(18, 183, 106, 0.2)",
    textColor: "text-success-500 dark:text-emerald-400",
  },
  warning: {
    InnerColor: "#F79009",
    OuterColor: "rgba(247, 144, 9, 0.2)",
    textColor: "text-warning-500 dark:text-amber-400",
  },
  danger: {
    InnerColor: "#F04438",
    OuterColor: "rgba(240, 68, 56, 0.2)",
    textColor: "text-error-500 dark:text-rose-400",
  },
  neutral: {
    InnerColor: "#717680",
    OuterColor: "rgba(156, 163, 175, 0.2)",
    textColor: "text-gray-500 dark:text-gray-300",
  },
  lightBlue: {
    InnerColor: "#207C97",
    OuterColor: "rgba(32, 124, 151, 0.2)",
    textColor: "text-[#207C97] dark:text-secondary-light",
  },
};

const resolveVariant = (variant) =>
  variantClassesWithoutBg[variant] ? variant : "neutral";

// check if tooltip prop is valid
const hasTooltipProp = (value) => {
  if (value == null || value === false) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

export const StatusChip = ({
  hideTitle = false,
  label = "",
  variant = "success",
  tooltipTitle = undefined,
  tooltipContent = undefined,
  tooltipPlacement = "top",
  tooltipCloseDelay = undefined,
  tooltipPanelClassName = undefined,
  className = "",
  withoutBg = false,
}) => {

  const hasTooltip =
    hasTooltipProp(tooltipTitle) || hasTooltipProp(tooltipContent);
  const resolvedVariant = resolveVariant(variant);
  const withoutBgStyles = variantClassesWithoutBg[resolvedVariant];
  const formattedLabel =
    String(label ?? "")
      .charAt(0)
      .toUpperCase() + String(label ?? "").slice(1);

  const chip = (
    <span
      className={cn(
        "inline-flex items-center rounded-lg px-2.5 py-0.5 text-xs font-normal",
        hasTooltip && "cursor-pointer",
        variantClassesWithBg[resolvedVariant],
        className,
      )}
    >
      {formattedLabel}{" "}
      {hasTooltip && <Info size={12} strokeWidth={1.7} className="ml-1" />}
    </span>
  );

  const withoutBgChip = (
    <span
      className={cn(
        "inline-flex items-center text-[9.8px] font-normal gap-x-1.5",
        hasTooltip && "cursor-pointer",
        withoutBgStyles.textColor,
        className,
      )}
    >
      <Eclipse
        InnerColor={withoutBgStyles.InnerColor}
        OuterColor={withoutBgStyles.OuterColor}
      />
      {!hideTitle && formattedLabel}{" "}
      {hasTooltip && <Info size={12} strokeWidth={1.7} className="ml-1" />}
    </span>
  );

  if (!hasTooltip) {
    return withoutBg ? withoutBgChip : chip;
  }

  return (
    <CustomCommonTooltip
      placement={tooltipPlacement}
      showArrow
      closeDelay={tooltipCloseDelay}
      className={tooltipPanelClassName}
      content={
        <div className="space-y-2">
          {hasTooltipProp(tooltipTitle) && (
            <div className="font-semibold text-[13px] text-gray-900 dark:text-gray-100 text-wrap break-words leading-snug">
              {tooltipTitle}
            </div>
          )}
          {hasTooltipProp(tooltipContent) &&
            (typeof tooltipContent === "string" ? (
              <div className="text-xs text-gray-500 dark:text-gray-300 text-wrap break-words leading-relaxed">
                {renderTextWithLinks(tooltipContent)}
              </div>
            ) : (
              <div className="min-w-0">{tooltipContent}</div>
            ))}
        </div>
      }
    >
      {withoutBg ? withoutBgChip : chip}
    </CustomCommonTooltip>
  );
};
